import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardLayoutMain from "../../../layout/dashboard/DashboardLayoutMain";
import { Button } from "../../../components/custom/custom-button";
import RichTextContent from "../../../components/fragment/rich-text-editor/RichTextContent";
import type { AppDispatch } from "../../../redux/store";
import { NotificationCampaignDetails } from "../../../redux/type";
import {
  deleteNotificationCampaign,
  getNotificationCampaign,
} from "../../../redux/notificationCampaign";
import { ALL_ROUTES } from "../../../routes/AllRoutes";
import { formatDateTime } from "./NotificationManagementIndex";
import DeleteNotificationCampaignModal from "./DeleteNotificationCampaignModal";

export default function NotificationCampaignView() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const campaignId = (location?.state as { id?: string })?.id;

  const [campaign, setCampaign] = useState<NotificationCampaignDetails | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const goBackToList = () =>
    navigate(ALL_ROUTES?.DASHBOARD_NOTIFICATION_MANAGEMENT);

  useEffect(() => {
    if (!campaignId) {
      goBackToList();
      return;
    }

    const fetchCampaign = async () => {
      setLoading(true);
      try {
        const result = await dispatch(
          getNotificationCampaign(campaignId) as any,
        ).unwrap();
        setCampaign(result?.data?.data ?? null);
      } catch (error) {
        console.error("Failed to fetch campaign details:", error);
        setCampaign(null);
      } finally {
        setLoading(false);
      }
    };
    fetchCampaign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const handleEdit = () => {
    if (!campaign) return;
    navigate(ALL_ROUTES?.DASHBOARD_NOTIFICATION_MANAGEMENT_CREATE, {
      state: { action: "update", details: campaign },
    });
  };

  const handleDeleteConfirm = async () => {
    if (!campaign) return;
    setDeleting(true);
    try {
      await dispatch(deleteNotificationCampaign(campaign.id) as any).unwrap();
      goBackToList();
    } catch (error) {
      console.error("Failed to delete campaign:", error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <DashboardLayoutMain back title="Campaign Overview" loading={loading}>
      {campaign && (
        <section className="mx-[2rem] mt-[15px] pb-[40px] max-w-[900px]">
          {/* sticky header row — mirrors the Edit-role header pattern: title
              on the left, primary actions on the right */}
          <div className="sticky top-0 z-10 flex items-center justify-between bg-[#F8F9FF] py-[12px]">
            <p className="text-[20px] font-bold text-[#1E1E1E]">
              {campaign.title}
            </p>
            <div className="flex items-center gap-[12px]">
              {campaign.status === "draft" && (
                <Button onClick={handleEdit}>Edit</Button>
              )}
              <Button variant="destructive" onClick={() => setDeleteModalOpen(true)}>
                Delete
              </Button>
            </div>
          </div>

          <div className="space-y-[16px] mt-[16px]">
            {/* Notification */}
            <section className="bg-white border border-[#E4E4E7] rounded-[8px] p-[24px]">
              <h3 className="mb-3 text-[16px] font-bold text-[#1E1E1E]">
                Notification
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DetailItem label="Title" value={campaign.title} />
                <DetailItem label="Type" value={campaign.type} />
                <DetailItem label="Status" value={campaign.status} />
                <DetailItem label="Created By" value={campaign.created_by?.name} />
              </div>
            </section>

            {/* Message */}
            <section className="bg-white border border-[#E4E4E7] rounded-[8px] p-[24px]">
              <h3 className="mb-3 text-[16px] font-bold text-[#1E1E1E]">
                Message
              </h3>
              <div className="rounded-lg bg-[#F8F8F8] p-4">
                <RichTextContent html={campaign.body} className="text-[14px] text-[#1E1E1E]" />
              </div>
            </section>

            {/* Channels */}
            <section className="bg-white border border-[#E4E4E7] rounded-[8px] p-[24px]">
              <h3 className="mb-3 text-[16px] font-bold text-[#1E1E1E]">
                Channels
              </h3>
              <div className="space-y-2">
                {campaign.channels?.map((channel) => (
                  <div
                    key={channel.channel}
                    className="flex items-center justify-between rounded-lg border border-[#E4E4E7] p-3"
                  >
                    <div>
                      <p className="text-[14px] font-medium capitalize">
                        {channel.channel}
                      </p>
                      <p className="text-[13px] text-[#758089]">
                        Recipient type: {channel.recipient_type}
                      </p>
                    </div>
                    <div className="flex flex-col gap-[5px] text-right text-xs">
                      <p>Sent: {channel.sent_count}</p>
                      <p className="text-red-500">Failed: {channel.failed_count}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Audience */}
            <section className="bg-white border border-[#E4E4E7] rounded-[8px] p-[24px]">
              <h3 className="mb-3 text-[16px] font-bold text-[#1E1E1E]">
                Audience
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DetailItem label="Audience Type" value={campaign.audience?.type} />
                <DetailItem
                  label="Estimated Recipients"
                  value={
                    campaign.audience?.estimated_recipient_count !== undefined
                      ? String(campaign.audience.estimated_recipient_count)
                      : undefined
                  }
                />
              </div>
            </section>

            {/* Schedule */}
            <section className="bg-white border border-[#E4E4E7] rounded-[8px] p-[24px]">
              <h3 className="mb-3 text-[16px] font-bold text-[#1E1E1E]">
                Schedule
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DetailItem label="Schedule Type" value={campaign.schedule?.type} />
                <DetailItem
                  label="Recurring"
                  value={campaign.schedule?.is_recurring ? "Yes" : "No"}
                />
                <DetailItem
                  label="Scheduled At"
                  value={
                    campaign.schedule?.scheduled_at
                      ? formatDateTime(campaign.schedule.scheduled_at)
                      : "Immediate"
                  }
                />
                <DetailItem
                  label="Last Sent"
                  value={
                    campaign.schedule?.last_sent_at
                      ? formatDateTime(campaign.schedule.last_sent_at)
                      : "—"
                  }
                />
              </div>
            </section>

            {/* Action button */}
            {campaign.action_button?.enabled && (
              <section className="bg-white border border-[#E4E4E7] rounded-[8px] p-[24px]">
                <h3 className="mb-3 text-[16px] font-bold text-[#1E1E1E]">
                  Action Button
                </h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <DetailItem label="Label" value={campaign.action_button.label} />
                  <DetailItem label="URL" value={campaign.action_button.url} />
                </div>
              </section>
            )}
          </div>
        </section>
      )}

      <DeleteNotificationCampaignModal
        visible={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        campaign={campaign}
        loading={deleting}
      />
    </DashboardLayoutMain>
  );
}

const DetailItem = ({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) => {
  return (
    <div>
      <p className="text-[13px] text-[#758089]">{label}</p>
      <p className="mt-1 break-words text-[14px] font-medium text-[#1E1E1E]">
        {value || "—"}
      </p>
    </div>
  );
};
